import { z } from 'zod'
import { BRAND } from './brand'

/**
 * Каталог ошибок и формат `application/problem+json` (RFC 9457) из docs/06 §1.
 * Коды — **стабильный контракт для фронта**: фронт ветвится по `code`,
 * а не по `status` и уж точно не по тексту.
 */

export const PROBLEM_CONTENT_TYPE = 'application/problem+json'

export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHORIZED',
  'TOTP_REQUIRED',
  'FORBIDDEN_ROLE',
  'NOT_FOUND',
  'MC_ACCOUNT_IN_USE',
  'BOT_ALREADY_IN_STATE',
  'LIMIT_EXCEEDED',
  'EGRESS_BLOCKED',
  'UNSUPPORTED_MC_VERSION',
  'RUNNER_UNAVAILABLE',
  'RATE_LIMITED',
  // 5xx: без них фронт вынужден маппить любую внутреннюю ошибку
  // в RUNNER_UNAVAILABLE, что семантически неверно
  'SIGNUP_CLOSED',
  'INTERNAL_ERROR',
  'UPSTREAM_UNAVAILABLE'
] as const

export const ErrorCode = z.enum(ERROR_CODES)
export type ErrorCode = z.infer<typeof ErrorCode>

/** HTTP-статус по коду — таблица из docs/06 §1. */
export const ERROR_STATUS: Readonly<Record<ErrorCode, number>> = {
  VALIDATION_FAILED: 400,
  UNAUTHORIZED: 401,
  TOTP_REQUIRED: 401,
  FORBIDDEN_ROLE: 403,
  NOT_FOUND: 404,
  MC_ACCOUNT_IN_USE: 409,
  BOT_ALREADY_IN_STATE: 409,
  LIMIT_EXCEEDED: 409,
  EGRESS_BLOCKED: 422,
  UNSUPPORTED_MC_VERSION: 422,
  RUNNER_UNAVAILABLE: 503,
  RATE_LIMITED: 429,
  SIGNUP_CLOSED: 403,
  INTERNAL_ERROR: 500,
  UPSTREAM_UNAVAILABLE: 502
}

/** Человекочитаемый `title` по умолчанию (локаль по умолчанию — ru, docs/05 `user.locale`). */
export const ERROR_TITLES: Readonly<Record<ErrorCode, string>> = {
  VALIDATION_FAILED: 'Некорректные данные запроса',
  UNAUTHORIZED: 'Требуется аутентификация',
  TOTP_REQUIRED: 'Нужен второй фактор',
  FORBIDDEN_ROLE: 'Недостаточно прав',
  NOT_FOUND: 'Ресурс не найден',
  MC_ACCOUNT_IN_USE: 'MC-аккаунт уже используется',
  BOT_ALREADY_IN_STATE: 'Бот уже в этом состоянии',
  LIMIT_EXCEEDED: 'Превышен лимит плана',
  EGRESS_BLOCKED: 'Адрес запрещён политикой egress',
  UNSUPPORTED_MC_VERSION: 'Версия Minecraft не поддержана ядром',
  RUNNER_UNAVAILABLE: 'Нет доступного runner’а',
  RATE_LIMITED: 'Слишком много запросов',
  SIGNUP_CLOSED: 'Регистрация закрыта',
  INTERNAL_ERROR: 'Внутренняя ошибка',
  UPSTREAM_UNAVAILABLE: 'Сервис недоступен'
}

/** Одна ошибка валидации: путь в теле запроса + сообщение (`VALIDATION_FAILED.errors[]`). */
export const ProblemFieldError = z.object({
  path: z.string(),
  message: z.string()
})
export type ProblemFieldError = z.infer<typeof ProblemFieldError>

export const ProblemDetails = z.object({
  /** `https://apiary.local/errors/mc-account-in-use` */
  type: z.string(),
  title: z.string(),
  status: z.number().int().min(400).max(599),
  code: ErrorCode,
  detail: z.string().optional(),
  /** Путь запроса, напр. `/api/bots/clx.../start`. */
  instance: z.string().optional(),
  traceId: z.string().optional(),
  /** Только для `VALIDATION_FAILED`. */
  errors: z.array(ProblemFieldError).optional(),
  /** Доп. контекст кода: `{ limit, current }`, `{ supported: [...] }`. */
  meta: z.record(z.unknown()).optional()
})
export type ProblemDetails = z.infer<typeof ProblemDetails>

/** `MC_ACCOUNT_IN_USE` → `mc-account-in-use`. */
export function errorCodeToSlug(code: ErrorCode): string {
  return code.toLowerCase().replace(/_/g, '-')
}

/** `MC_ACCOUNT_IN_USE` → `https://apiary.local/errors/mc-account-in-use`. */
export function problemTypeUri(code: ErrorCode): string {
  return `${BRAND.errorTypeBaseUrl}/${errorCodeToSlug(code)}`
}

export interface ProblemInit {
  detail?: string
  instance?: string
  traceId?: string
  errors?: ProblemFieldError[]
  meta?: Record<string, unknown>
  /** Переопределение заголовка (например, локализованного). */
  title?: string
  /** Переопределение статуса — нужно крайне редко, по умолчанию берётся из `ERROR_STATUS`. */
  status?: number
}

/**
 * Собирает тело `application/problem+json`. Единственный способ создавать ошибки
 * в API — чтобы `type`/`status`/`title` не расходились с каталогом.
 */
export function problemDetails(code: ErrorCode, init: ProblemInit = {}): ProblemDetails {
  const problem: ProblemDetails = {
    type: problemTypeUri(code),
    title: init.title ?? ERROR_TITLES[code],
    status: init.status ?? ERROR_STATUS[code],
    code
  }
  if (init.detail !== undefined) problem.detail = init.detail
  if (init.instance !== undefined) problem.instance = init.instance
  if (init.traceId !== undefined) problem.traceId = init.traceId
  if (init.errors !== undefined) problem.errors = init.errors
  if (init.meta !== undefined) problem.meta = init.meta
  return problem
}

/** Type guard для клиента: пришёл ли в ответе наш problem+json. */
export function isProblemDetails(value: unknown): value is ProblemDetails {
  return ProblemDetails.safeParse(value).success
}

/** zod-ошибка → `errors[]` для `VALIDATION_FAILED`. */
export function zodIssuesToFieldErrors(issues: readonly z.ZodIssue[]): ProblemFieldError[] {
  return issues.map(issue => ({
    path: issue.path.join('.'),
    message: issue.message
  }))
}
